import { proxy } from 'valtio/vanilla'
import { debounce } from 'lodash-es'
import { getSharedState } from '../valtio'

// Framework-agnostic owner of the page-layout state and behavior. Unlike the
// other modules here this is the source of truth, not a mirror: the state itself,
// the sidebar resize handlers (DOM math), and the persistence orchestration all
// live here so any consumer drives the same layout.
//
// Two things are app-coupled and injected via `configurePageLayout`, keeping
// this package free of app imports:
//   - persistence  — reads/writes the per-view layout saved in user settings
//                     (the app wires this to its settings API + session object)
//   - screenLg     — the Tailwind `lg` breakpoint in px (togglePageNav guard)
//
// NOTE: this module reads and writes the DOM directly (document.body styles,
// CSS custom properties on documentElement, window listeners) and depends on
// markup it does not own via [data-testid="page-layout-sections"]. See
// MIGRATION-NOTES.md.

// The app's top-level views. This lives here rather than in the app because
// page-layout is keyed by it: layouts persist per view. The app re-exports it
// from `@/types/pageLayout` so existing import paths are unchanged.
export enum RootViews {
  Dashboard = 'dashboard',
  Conversations = 'conversations',
  Activity = 'activity',
  Coaching = 'coaching',
  Workspaces = 'workspaces',
  Settings = 'settings',
  Tasks = 'tasks',
}

export const TICKET_VIEWS = [RootViews.Conversations, RootViews.Tasks, RootViews.Activity]

export interface LayoutState {
  leftSidebar: string | undefined
  leftSidebarSize: number
  rightSidebar: string | undefined
  rightSidebarSize: number
  showPageNav: boolean
  contentSidebarSize: number
}

export interface State extends LayoutState {
  view: RootViews | undefined
  resizing: boolean
}

type SizeStateKey = 'leftSidebarSize' | 'rightSidebarSize' | 'contentSidebarSize'
export type PersistedLayouts = Record<string, Partial<LayoutState>>

export interface PageLayoutPersistence {
  // The per-view layout map saved in user settings (or undefined if none yet).
  getPersistedLayouts: () => PersistedLayouts | undefined
  // Persist the full per-view map (settings API + wherever the app caches it).
  persist: (layouts: PersistedLayouts) => Promise<void> | void
  // Tailwind `lg` breakpoint in px, for the togglePageNav small-screen guard.
  screenLg: number
}

const SIDEBAR_WIDTH = 320
const MIN_SIDEBAR_WIDTH = 280
const MIN_CONTENT_WIDTH = 360
const TRANSITION_DURATION = 160
const WINDOW_RESIZE_DEBOUNCE = 300

// Views that get an extra content-sidebar.
const CONTENT_SIDEBAR_VIEWS = [RootViews.Conversations, RootViews.Tasks, RootViews.Activity]

const initialState: State = {
  view: undefined,
  leftSidebar: undefined,
  leftSidebarSize: SIDEBAR_WIDTH,
  rightSidebar: undefined,
  rightSidebarSize: SIDEBAR_WIDTH,
  showPageNav: true,
  resizing: false,
  contentSidebarSize: SIDEBAR_WIDTH,
}

const defaultState = Object.assign({}, initialState)

export const pageLayoutState = getSharedState('page-layout', () => proxy<State>({ ...initialState }))

let persistence: PageLayoutPersistence | undefined

// Wire the app-coupled persistence + breakpoint. Called once at app boot.
export const configurePageLayout = (config: PageLayoutPersistence) => {
  persistence = config
}

const persistState = async () => {
  const view = pageLayoutState.view
  if (!persistence || !view) return

  const state = pageLayoutState
  const newPageLayout: PersistedLayouts = {
    ...(persistence.getPersistedLayouts() || {}),
    [view]: {
      leftSidebar: state.leftSidebar,
      leftSidebarSize: state.leftSidebarSize,
      rightSidebar: state.rightSidebar,
      rightSidebarSize: state.rightSidebarSize,
      contentSidebarSize: state.contentSidebarSize,
      showPageNav: Boolean(state.showPageNav),
    },
  }

  await persistence.persist(newPageLayout)
}

export const resetLayout = (view: RootViews, defaults?: Partial<LayoutState>) => {
  let persistedState: Partial<LayoutState> = {}
  const filteredDefaults: Partial<LayoutState> = {}
  const pageLayout = persistence?.getPersistedLayouts()

  if (pageLayout && view in pageLayout) {
    persistedState = pageLayout[view]
  }

  if (defaults) {
    // Apply defaults only for keys that exist in persistedState
    for (const key of Object.keys(defaults) as (keyof LayoutState)[]) {
      if (key in persistedState && key in defaults) {
        ;(filteredDefaults as Record<string, unknown>)[key] = (defaults as Record<string, unknown>)[key]
      }
    }
  }

  // Object.assign only overwrites keys present in the source, so this relies on
  // the merged object always being a *complete* State (defaultState is spread
  // first and has every key) rather than a partial merge.
  Object.assign(pageLayoutState, { ...defaultState, view, ...filteredDefaults, ...persistedState })
}

interface ResizeHandler {
  ref?: HTMLElement
  sizeStateKey: SizeStateKey
  styleProp?: string
  initialPageX: number
  isFromRight: boolean
}

export const sidebarResizer = {
  startResizing: (ref?: HTMLElement) => {
    pageLayoutState.resizing = true
    if (ref?.parentElement) ref.parentElement.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = document.body.style.webkitUserSelect = 'none'
  },
  stopResizing: (ref?: HTMLElement) => {
    pageLayoutState.resizing = false
    if (ref?.parentElement) ref.parentElement.style.userSelect = ''
    document.body.style.cursor = 'unset'
    document.body.style.userSelect = document.body.style.webkitUserSelect = 'unset'
    persistState()
  },
  hasContentSidebar(view: RootViews | undefined) {
    return view !== undefined && CONTENT_SIDEBAR_VIEWS.includes(view)
  },
  resizingAllowed() {
    const layoutSections = document.querySelector('[data-testid="page-layout-sections"]')

    return layoutSections?.scrollWidth === layoutSections?.clientWidth
  },
  getMaxAdditionalWidth() {
    const layoutSections = document.querySelector('[data-testid="page-layout-sections"]')
    let sum = 0
    if (sidebarResizer.hasContentSidebar(pageLayoutState.view)) {
      sum += pageLayoutState.contentSidebarSize
    }
    if (pageLayoutState.rightSidebar) sum += pageLayoutState.rightSidebarSize
    if (pageLayoutState.leftSidebar) {
      sum += pageLayoutState.leftSidebar === 'collapsed' ? 64 : pageLayoutState.leftSidebarSize
    }

    return (layoutSections?.clientWidth ?? 0) - MIN_CONTENT_WIDTH - sum
  },
  createResizeHandler: ({ ref, sizeStateKey, styleProp, initialPageX, isFromRight }: ResizeHandler) => {
    sidebarResizer.startResizing(ref)
    const initialWidth = pageLayoutState[sizeStateKey] || 320
    let oldPageX = 0

    const onMouseMove = ({ pageX }: MouseEvent) => {
      const resizingAllowed = sidebarResizer.resizingAllowed()
      if (
        (!resizingAllowed && isFromRight && pageX > oldPageX) ||
        (!resizingAllowed && !isFromRight && pageX < oldPageX) ||
        resizingAllowed ||
        oldPageX === 0
      ) {
        oldPageX = pageX
      }

      if (
        sizeStateKey === 'rightSidebarSize' &&
        sidebarResizer.getMaxAdditionalWidth() === 0 &&
        initialPageX - pageX > 0 &&
        pageLayoutState.contentSidebarSize > MIN_SIDEBAR_WIDTH
      ) {
        pageLayoutState.contentSidebarSize = Math.max(
          pageLayoutState.contentSidebarSize + pageX - initialPageX,
          MIN_SIDEBAR_WIDTH,
        )
        document.documentElement.style.setProperty(
          '--content-sidebar-width',
          `${pageLayoutState.contentSidebarSize}px`,
        )
      }

      if (
        sizeStateKey === 'contentSidebarSize' &&
        pageLayoutState.contentSidebarSize === MIN_SIDEBAR_WIDTH &&
        initialPageX - pageX < 0 &&
        pageLayoutState.rightSidebarSize > MIN_SIDEBAR_WIDTH &&
        pageLayoutState.rightSidebar
      ) {
        pageLayoutState.rightSidebarSize = Math.max(
          pageLayoutState.rightSidebarSize + initialPageX - pageX,
          MIN_SIDEBAR_WIDTH,
        )
        document.documentElement.style.setProperty('--right-sidebar-width', `${pageLayoutState.rightSidebarSize}px`)
      }

      const maxWidth = pageLayoutState[sizeStateKey] + sidebarResizer.getMaxAdditionalWidth()
      const sizeChange = isFromRight ? initialPageX - oldPageX : oldPageX - initialPageX
      pageLayoutState[sizeStateKey] = Math.min(Math.max(initialWidth + sizeChange, MIN_SIDEBAR_WIDTH), maxWidth)
      if (styleProp) document.documentElement.style.setProperty(styleProp, `${pageLayoutState[sizeStateKey]}px`)
    }

    const onResizeDebounced = debounce(
      () => sidebarResizer.onWindowResize(sizeStateKey, styleProp),
      WINDOW_RESIZE_DEBOUNCE,
    )

    const onMouseUp = () => {
      sidebarResizer.stopResizing(ref)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('resize', onResizeDebounced)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    window.addEventListener('resize', onResizeDebounced)
  },
  onWindowResize: (sizeStateKey: SizeStateKey, styleProp?: string) => {
    pageLayoutState[sizeStateKey] = initialState[sizeStateKey]
    if (styleProp) document.documentElement.style.setProperty(styleProp, `${pageLayoutState[sizeStateKey]}px`)
  },
}

export const handleContentSidebarResize = ({ pageX: initialPageX }: MouseEvent, ref: HTMLElement) => {
  sidebarResizer.createResizeHandler({
    ref,
    sizeStateKey: 'contentSidebarSize',
    styleProp: '--content-sidebar-width',
    initialPageX,
    isFromRight: true,
  })
}

export const handleLeftSidebarResize = ({ pageX: initialPageX }: MouseEvent, ref: HTMLElement) => {
  sidebarResizer.createResizeHandler({
    ref,
    sizeStateKey: 'leftSidebarSize',
    styleProp: '--left-sidebar-width',
    initialPageX,
    isFromRight: false,
  })
}

export const handleRightSidebarResize = ({ pageX: initialPageX }: MouseEvent, ref: HTMLElement) => {
  sidebarResizer.createResizeHandler({
    ref,
    sizeStateKey: 'rightSidebarSize',
    styleProp: '--right-sidebar-width',
    initialPageX,
    isFromRight: true,
  })
}

const setSidebarSizes = (state: Partial<State>) => {
  if (sidebarResizer.resizingAllowed()) return

  if (state.leftSidebar === 'collapsed') {
    pageLayoutState.leftSidebarSize = initialState.leftSidebarSize
  }

  if (
    state.leftSidebar === 'list' &&
    pageLayoutState.contentSidebarSize > initialState.contentSidebarSize &&
    !state.rightSidebar
  ) {
    pageLayoutState.contentSidebarSize = initialState.contentSidebarSize
  }

  if (state.rightSidebar === 'review' && pageLayoutState.contentSidebarSize > initialState.contentSidebarSize) {
    pageLayoutState.contentSidebarSize = initialState.contentSidebarSize

    if (!sidebarResizer.resizingAllowed() && pageLayoutState.rightSidebarSize > initialState.rightSidebarSize) {
      pageLayoutState.rightSidebarSize = initialState.rightSidebarSize
    }
  }
}

/**
 * @param view Required if persist is true, to prevent overwriting other views' state
 * @param persist Whether to persist the new state to the user's settings
 */
export const setLayoutState = async (view: RootViews, state: Partial<State>, persist = true) => {
  if (pageLayoutState.view && pageLayoutState.view !== view) {
    resetLayout(view)
  }

  Object.assign(pageLayoutState, { view }, state)
  if (persist) await persistState()

  setTimeout(() => {
    if (sidebarResizer.hasContentSidebar(view)) setSidebarSizes(state)
  }, TRANSITION_DURATION)
}

export const togglePageNav = (state: boolean) => {
  // No hardcoded fallback: the breakpoint is a design token owned by
  // ui-kit/tailwind.config.cjs (theme.screens.lg), which this package cannot
  // import. Duplicating it here means it silently drifts — an earlier version of
  // this code guessed 1024 while the real value is 1320, so page-nav toggling
  // was permitted in a 296px band where the app blocked it. If the host has not
  // called configurePageLayout, fail closed rather than guess.
  const screenLg = persistence?.screenLg
  if (screenLg === undefined || window.innerWidth < screenLg) return

  pageLayoutState.showPageNav = state
}
